import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./HandEyeCoordination.css";

const HandEyeCoordination = () => {

  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(null);
  const [joystickError, setJoystickError] = useState(false); // ✅ joystick check popup

  // ================= ACCESS CHECK =================
  useEffect(() => {
    const email = localStorage.getItem("user_email");

    if (!email) {
      navigate("/");
      return;
    }

    axios
      .get(`/check-access?email=${email}&test=hand_eye`)
      .then(res => {
        if (res.data.allowed) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      })
      .catch(() => navigate("/"));
  }, [navigate]);

  // ================= JOYSTICK CHECK =================
  const getConnectedGamepad = () => {
    const gps = navigator.getGamepads ? navigator.getGamepads() : [];
    for (let i = 0; i < gps.length; i++) {
      if (gps[i]) return gps[i];
    }
    return null;
  };

  const handleStartTest = () => {
    const gp = getConnectedGamepad();
    if (!gp) {
      // ✅ No joystick → show error popup
      setJoystickError(true);
    } else {
      // ✅ Joystick connected → go to game
      navigate("/hand-eye/square");
    }
  };

  // ================= LOADING =================
  if (hasAccess === null) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h3>Loading...</h3>
      </div>
    );
  }

  // ================= ACCESS DENIED =================
  if (hasAccess === false) {
    return (
      <div className="access-denied-container">
        <h2>🚫 Access Denied</h2>
        <p>You don't have access to this test</p>
        <button onClick={() => navigate("/")}>Go Back</button>
      </div>
    );
  }

  // ================= MAIN UI =================
  return (
    <div className="rg-login-wrapper">

      {/* ================= JOYSTICK ERROR POPUP ================= */}
      {joystickError && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "100%",
          background: "rgba(0,0,0,0.65)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999
        }}>
          <div style={{
            background: "white",
            borderRadius: "16px",
            padding: "40px 36px",
            textAlign: "center",
            maxWidth: "340px",
            width: "90%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: "56px", marginBottom: "16px" }}>🎮</div>
            <h2 style={{
              margin: "0 0 10px",
              fontSize: "22px",
              color: "#1a1a2e"
            }}>
              Joystick Required
            </h2>
            <p style={{
              color: "#666",
              fontSize: "15px",
              lineHeight: "1.6",
              marginBottom: "28px"
            }}>
              This test can only be played using a joystick / gamepad.
              Please connect your joystick and try again.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <button
                onClick={() => {
                  // ✅ Retry — check again if joystick connected now
                  const gp = getConnectedGamepad();
                  if (gp) {
                    setJoystickError(false);
                    navigate("/hand-eye/square");
                  }
                  // If still not connected, popup stays
                }}
                style={{
                  padding: "12px",
                  background: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer"
                }}
              >
                I've connected — Try Again
              </button>
              <button
                onClick={() => setJoystickError(false)}
                style={{
                  padding: "12px",
                  background: "#f0f0f0",
                  color: "#444",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "15px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= HEADER ================= */}
      <div className="reaction-header-card">

        <h2 className="reaction-title">Hand-Eye Coordination Test</h2>

        <p className="reaction-subtitle">
          Hand-eye coordination measures how well your eyes and hands work together
          to perform tasks quickly and accurately.
          It trains the brain to
          <strong> "See → Process → Act."</strong>
        </p>

        <div className="reaction-pill">
          Test type: <strong>Coordination → Reaction + Precision</strong>
        </div>

        <hr className="reaction-divider" />

        {/* ================= WHY SECTION ================= */}
        <div className="reaction-why-card">

          <h3 className="why-title">
            Why hand-eye coordination matters
          </h3>

          <p className="why-description">
            Strong hand-eye coordination improves reaction speed, accuracy,
            and control. It is essential in sports, gaming, driving, and
            everyday activities that require quick responses.
          </p>

          <div className="why-tags">
            <span>Faster reaction time</span>
            <span>Better accuracy</span>
            <span>Improved motor skills</span>
            <span>Enhanced reflexes</span>
            <span>Better control &amp; timing</span>
          </div>

        </div>
      </div>

      {/* ================= HOW GAME WORKS ================= */}
      <div className="how-card">

        <h2 className="how-title">How the game works</h2>

        <ul className="how-list">
          <li>
            The test includes four levels: Square, Line, Circle, and Curve.
          </li>
          <li>
            In each level, a path will appear which you must trace accurately.
          </li>
          <li>
            Follow the path in a clockwise direction to complete the level.
          </li>
          <li>
            Use the joystick for movement:
            <br />• Right stick → Left / Right control
            <br />• Left stick → Up / Down control
          </li>
          <li>
            Performance is based on speed and accuracy.
          </li>
        </ul>

        <p className="how-note">
          ⚠️ This test requires a joystick / gamepad to play. Mobile touch is not supported.
        </p>

      </div>

      {/* ================= START TEST ================= */}
      <div className="start-test-section">

        <h2>Ready to test your coordination?</h2>

        {/* ✅ handleStartTest checks joystick before navigating */}
        <button
          className="start-test-btn"
          onClick={handleStartTest}
        >
          Start Test
        </button>

      </div>

    </div>
  );
};

export default HandEyeCoordination;
